import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { Sort } from '@angular/material/sort';
import { MatLegacyTable as MatTable } from '@angular/material/legacy-table';
import { ALIGN } from 'components/analyze/analyze-common';
import { Subject, filter, takeUntil } from 'rxjs';
import { DefinitionService } from 'services/definition/definition.service';
import { IDeviceName } from "services/definition/interface";
import { DTCService, DTC_Message, GetDTCRequestStatus, GetDataRequestResult } from 'services/dtc-service/dtc.service';
import { Logger } from 'src/app/common/utility/logger/logger';
import { SubscriptionService } from "src/app/services/subscription/subscription.service";
import { CountryCode } from 'src/app/services/translation/country-code';
import { TranslationService } from 'src/app/services/translation/translation.service';
import { COLUMN_ID, DTCDisplaySettings, DTCSettings, DTCWidgetSettingsComponent } from './dtc-widget-settings/dtc-widget-settings.component';

export const enum DTC_Status {
  Inactive = 0,
  Active = 1
}

export const enum DTC_AccommodationLevel {
  NORMAL = 0,
  WARNING = 1,
  DERATE = 2,
  DISCONNECT = 3,
  E_STOP = 4
}

interface DTC_EVENT {
  sourceId: string,
  deviceId: string,
  dtcList: Array<DTC>
}

interface DTC {
  secondDeviceId: string,
  deviceDisplay: string,
  eventTimestamp: number,
  code: number,
  codeDisplay: string,
  status: DTC_Status,
  accommodationLevel: DTC_AccommodationLevel,
  occurrenceCount: number,
}

interface DTC_LIST {
  sourceId: string,
  deviceId: string,
  secondDeviceId: string,
  deviceDisplay: string,
  eventTimestamp: number,
  code: number,
  codeDisplay: string,
  status: DTC_Status,
  accommodationLevel: DTC_AccommodationLevel,
  occurrenceCount: number,
}


@Component({
  selector: 'dtc-widget',
  templateUrl: './dtc-widget.component.html',
  styleUrls: ['./dtc-widget.component.scss']
})
export class DTCWidgetComponent implements OnInit, OnDestroy, OnChanges {
  @Input() dtcSettings!: DTCSettings;
  @Output() dtcSettingsChange = new EventEmitter<DTCSettings>
  @Input() editMode!: boolean;
  @Output() deleteWidgetChange = new EventEmitter<boolean>()
  private unsubscribe: Subject<void> = new Subject();
  private languageCode: CountryCode | undefined = undefined;
  public dtcColumnDisplay: Array<DTCDisplaySettings> = [];
  public displayColumns: Array<string> = []
  public dtcList: Array<DTC_LIST> = []
  private rawDTC: Array<DTC_LIST> = []
  private deviceList: IDeviceName = {}
  private sort: Sort = { active: 'status', direction: 'desc' };
  private transactionId: string | undefined;
  public titleStyle!: string

  @ViewChild(MatTable) table!: MatTable<DTC_LIST>;

  constructor(
    private dialog: MatDialog,
    private translationService: TranslationService,
    private subscriptionService: SubscriptionService,
    private dtcService: DTCService
  ) { }

  public ngOnInit(): void {
    this.dtcColumnDisplay = DTCWidgetSettingsComponent.dtcGetDisplaySettings(this.dtcSettings.columnSettings)

    this.loadTranslations();

    // Setup Listern for getDTC
    this.dtcService.listenDTCupdate.pipe(takeUntil(this.unsubscribe)).
      subscribe((message: GetDataRequestResult) => {
        if (message.status === GetDTCRequestStatus.Success && message.transactionId === this.transactionId) {
          this.transactionId = undefined
          this.processReceivedDTClist(message.dtcList)
        }
      });

    // Setup Listener for Telemetry Messages
    this.subscriptionService.listenForDtcMessages().
      pipe(takeUntil(this.unsubscribe), filter(msg => (msg.sourceId == this.dtcSettings?.sourceId))).
      subscribe((message: any) => {
        const dtcEvent: DTC_EVENT = {
          sourceId: message.sourceId,
          deviceId: message.deviceId,
          dtcList: []
        }

        message.dtc.forEach((dtc: any) => {
          dtcEvent.dtcList.push({
            secondDeviceId: dtc.secDevId,
            deviceDisplay: this.getDeviceDisplay(message.deviceId, dtc.secDevId),
            eventTimestamp: dtc.et,
            code: parseInt(dtc.code),
            codeDisplay: DefinitionService.getDTCDisplayName(message.ltmi, parseInt(dtc.code)),
            status: parseInt(dtc.stat),
            accommodationLevel: parseInt(dtc.accLvl),
            occurrenceCount: parseInt(dtc.occurCnt)
          });
        });

        this.updateDTCList(dtcEvent);
      }); // socksetService.listen()

    this.titleStyle = "display: flex; flex-direction: column; " +
      "font-weight: " + this.dtcSettings.title.font.weight + ";" +
      "align-items: " + this.dtcSettings.title.align + ";" +
      "font-size: " + this.dtcSettings.title.font.size + "px;"

  } // ngOnInit()

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  } // ngOnDestroy()

  public ngOnChanges(changes: SimpleChanges): void {
    const DTCSettings = 'dtcSettings';

    if (!!changes[DTCSettings]) {
      const change = changes[DTCSettings];

      // If previous isn't undefined
      if (change.isFirstChange() || (change.previousValue !== change.currentValue)) {
        this.rawDTC = this.dtcList = []
        if (change.currentValue.sourceId) {
          this.transactionId = this.dtcService.getSourceDTCById(change.currentValue.sourceId)
          this.subscriptionService.appendDTCSubscription(change.currentValue.sourceId);
          if (change.isFirstChange()) {
            this.subscriptionService.sendSubscriptionRequest();
          } else {
            this.subscriptionService.sendSubscription()
          }
          this.deviceList = DefinitionService.getSourceDeviceList(change.currentValue.sourceId)
        }
        this.dtcColumnDisplay = DTCWidgetSettingsComponent.dtcGetDisplaySettings(change.currentValue.columnSettings)
        this.displayColumns = []
        this.dtcColumnDisplay.forEach((col: any) => {
          if (col.display)
            this.displayColumns.push(col.columnId)
        });
      } // If previous!= current
    } // if changes[DTCSettings]

  } // ngOnChanges()


  sortData(sort: Sort) {
    this.sort = sort;

    const data = this.rawDTC.slice();
    if (!sort.active || sort.direction === '') {
      this.dtcList = data;
      return;
    }

    this.dtcList = data.sort((a: any, b: any) => {
      const isAsc = sort.direction === 'asc';

      if (sort.active) {
        return this.compare(a[sort.active], b[sort.active], isAsc)
      }
      return 0
    });
  } // sortData()

  private compare(a: number | string, b: number | string, isAsc: boolean) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  } // compare()

  public getDTCdisplyValue(columnId: string, val: string | number): string {
    switch (columnId) {
      case COLUMN_ID.status:
        return "widgets.dtc-widget.status." + val
      case COLUMN_ID.accommodationLevel:
        return "widgets.dtc-widget.level." + val
      case COLUMN_ID.eventTimestamp:
        return this.getTimestamp(<string>val)
    }
    return <string>val;
  } // getDTCdisplyValue()

  public getColumnHeading(col: string): string {
    return "widgets.dtc-widget." + col
  } // getColumnHeading()

  public getTimestamp(timestamp: string): string {
    const date = new Date(parseInt(timestamp))
    return date.toLocaleDateString() + " " + date.toLocaleTimeString(undefined, { hour12: false })
  } // getTimestamp()

  // Drag cursor style
  public get cursorStyle(): string {
    return this.editMode ? "cursor: move;" : ""
  } // get cursorStyle()

  public tableHeight(): string {
    if (this.editMode)
      return "height: 94%;";
    // TODO: Add check if Widget Title, then do different heights
    return "height: 97%;";
  }

  public fontHeight(): number {
    return 16;
  }

  public fontStyle(): string {
    return "font-size: " + this.fontHeight() + "px;";
  }

  private getDeviceDisplay(deviceId: string, secondDeviceId: string): string {
    const deviceDisplay = this.deviceList[deviceId]
    if (!secondDeviceId) {
      return deviceDisplay + "<->" + this.deviceList[secondDeviceId]
    }

    return deviceDisplay
  } // getDeviceDisplay()


  private processReceivedDTClist(events: Array<DTC_Message>): void {
    this.rawDTC.splice(0);

    events.forEach((dtc: DTC_Message) => {
      const event: DTC_LIST = {
        sourceId: dtc.sourceId,
        deviceId: dtc.deviceId,
        secondDeviceId: dtc.secDevId,
        deviceDisplay: this.getDeviceDisplay(dtc.deviceId, dtc.secDevId),
        eventTimestamp: dtc.et,
        code: dtc.code,
        codeDisplay: DefinitionService.getDTCDisplayName(dtc.ltmi, dtc.code),
        status: dtc.stat,
        accommodationLevel: dtc.accLvl,
        occurrenceCount: dtc.occurCnt,
        // totalActiveTime: dtc.totalActiveTime
      }

      this.rawDTC.push(event)
    })

    this.sortData(this.sort)
    this.table.renderRows()
  } // processReceivedDTClist()

  private updateDTCList(events: DTC_EVENT): void {

    // Add update
    events.dtcList.forEach(event => {
      // See if the item already exists
      const found = this.dtcList.find(item => {
        if (
          (item.sourceId == events.sourceId) &&
          (item.deviceId == events.deviceId) &&
          (item.code == event.code)
        )
          return item;
        else
          return undefined;
      });

      // If not found, Add
      if (found == undefined) {
        const test: any = event;
        test['sourceId'] = events.sourceId
        test['deviceId'] = events.deviceId
        this.rawDTC.push(<DTC_LIST>test);
      } else { // If found, update
        found.eventTimestamp = event.eventTimestamp;
        found.occurrenceCount = event.occurrenceCount;
      }
    });

    // Get list of DTCs for Source/Device
    const currentList = this.rawDTC.filter(dtc =>
      ((events.sourceId == dtc.sourceId) && (events.deviceId == dtc.deviceId))
    )
    // See if the currentList is in the receivedList
    // If not, then delete from currentList...
    currentList.forEach(dtc => {
      if (!events.dtcList.find(item =>
        (dtc.code == item.code)
      )) {
        // Delete Item from currentList
        this.rawDTC.splice(this.rawDTC.findIndex(cur => cur.code == dtc.code), 1)
      }
    });

    this.sortData(this.sort)
    this.table.renderRows()
  } // updateDTCList()

  // Translation code
  private labelLast: string | undefined;
  private loadTranslations(): void {
    const label_last: string = 'widgets.table-widget.last';
    const labels: Array<string> = [
      label_last
    ];

    this.translationService.get(labels)
      .pipe(
        takeUntil(this.unsubscribe)
      ).subscribe(translations => {
        this.labelLast = translations[label_last];
      });
  } // loadTranslations()

  private watchLanguageChanges(): void {
    this.translationService.LanguageChange.pipe(
      takeUntil(this.unsubscribe),
      filter((languageCode: CountryCode) => {
        Logger.debug(`Language Filter CountryCode: ${languageCode}.Current: '${this.languageCode}'`)
        return languageCode !== this.languageCode
      })
    ).subscribe((languageCode: CountryCode) => {
      this.languageCode = languageCode
      this.loadTranslations();
    })
  } // watchLanguageChanges()

  public changeSettings(): void {
    const settingsDialog = this.dialog.open(DTCWidgetSettingsComponent, { disableClose: true, data: { settings: this.dtcSettings } });

    // Subscribe to the result
    settingsDialog.afterClosed().subscribe((res) => {
      if (res && res.event === 'apply') {
        this.dtcSettings = structuredClone(res.data.settings)
        this.dtcSettingsChange.emit(this.dtcSettings)

        this.titleStyle = "display: flex; flex-direction: column; " +
          "font-weight: " + this.dtcSettings.title.font.weight + ";" +
          "align-items: " + this.dtcSettings.title.align + ";" +
          "font-size: " + this.dtcSettings.title.font.size + "px;"

        this.dtcColumnDisplay = DTCWidgetSettingsComponent.dtcGetDisplaySettings(this.dtcSettings.columnSettings)
        this.displayColumns.splice(0);
        this.dtcColumnDisplay.forEach((col: any) => {
          if (col.display)
            this.displayColumns.push(col.columnId)
        });
      }
    });
  } // changeSettings()

  public deleteWidget(): void {
    this.deleteWidgetChange.emit(true)
  } // deleteWidget()

  public get getALIGN(): any {
    return ALIGN;
  } // getALIGN()

  public getTitle(): string {
    return this.dtcSettings.title.showTitle ? this.dtcSettings.title.text : ""
  } // getTitle()

  public getTitleStyle(): string {
    const format = "display: flex; flex-direction: column; " +
      "font-weight: " + this.dtcSettings.title.font.weight + ";" +
      "align-items: " + this.dtcSettings.title.align + ";" +
      "font-size: " + this.dtcSettings.title.font.size + "px;"
    return format
  } // getTitleStyle()

} // class DTCWidgetComponent
